import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";
import { useForm } from "@inertiajs/react";
import type { ReactNode } from "react";

export interface EndReason {
  value: string;
  label: string;
}

export const END_REASONS: EndReason[] = [
  { value: 'placed_successfully', label: 'Mentee was placed successfully' },
  { value: 'mentee_unresponsive', label: 'Mentee was unresponsive' },
  { value: 'no_longer_available', label: "I'm no longer available to mentor" },
  { value: 'other', label: 'Other' }
];

export const ADMIN_END_REASONS: EndReason[] = [
  { value: 'placed_successfully', label: 'Mentee was placed successfully' },
  { value: 'mentee_unresponsive', label: 'Mentee was unresponsive' },
  { value: 'no_longer_available', label: 'Mentor is no longer available' },
  { value: 'other', label: 'Other' }
];

interface EndMatchDialogProps {
  matchId: number;
  menteeName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  endpoint?: string;
  reasons?: EndReason[];
  description?: ReactNode;
}

export default function EndMatchDialog({
  matchId,
  menteeName,
  open,
  onOpenChange,
  endpoint = `/matches/${matchId}/end_match`,
  reasons = END_REASONS,
  description = <>End your mentorship with <span className="font-medium text-gray-700">{menteeName}</span></>
}: EndMatchDialogProps) {
  const { data, setData, patch, processing } = useForm({
    reason: '',
    placement_school: '',
    notes: ''
  })

  const handleSubmit = () => {
    patch(endpoint, {
      onSuccess: () => onOpenChange(false)
    })
  }

  const isValid = data.reason !== '' && (data.reason !== 'placed_successfully' || data.placement_school.trim() !== '') && (data.reason !== 'other' || data.notes.trim() !== '')

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>

      <DialogContent className="sm:max-w-lg bg-white">
        <DialogHeader>
          <DialogTitle>End Match</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          <div className="space-y-1.5">
            <Label>
              Reason <span className="text-red-500">*</span>
            </Label>
            <RadioGroup value={data.reason} onValueChange={(value) => setData('reason', value)}>
              {reasons.map((r) => (
                <div key={r.value} className="flex items-center space-x-2">
                  <RadioGroupItem value={r.value} id={`reason-${r.value}`} />
                  <Label htmlFor={`reason-${r.value}`} className="font-normal cursor-pointer">{r.label}</Label>
                </div>
              ))}
            </RadioGroup>
          </div>
          {data.reason === 'placed_successfully' && (
            <div className="space-y-1.5">
              <Label htmlFor="placement_school">
                Where was the mentee placed? <span className="text-red-500">*</span>
              </Label>
              <Input
                id="placement_school"
                value={data.placement_school}
                onChange={(e) => setData('placement_school', e.target.value)}
                placeholder="e.g., Emory University"
              />
            </div>
          )}
          {data.reason && (
            <div className="space-y-1.5">
              <Label htmlFor="notes">
                Notes{' '}
                {data.reason === 'other' ? (
                  <span className="text-red-500">*</span>
                ) : (
                  <span className="text-muted-foreground font-normal">(optional)</span>
                )}
              </Label>
              <Textarea
                id="notes"
                value={data.notes}
                onChange={(e) => setData('notes', e.target.value)}
                placeholder={data.reason === 'other' ? "Please explain the reason behind ending this match." : "Any additional information..."}
                rows={3}
              />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant={'outline'} onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button variant={'destructive'} disabled={!isValid || processing} onClick={handleSubmit}>
            {processing ? 'Ending...' : 'End Match'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
